
import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Users, Search, ChevronLeft, ChevronRight, ShieldAlert, Lock, CheckCircle, XCircle, X, Building2 } from "lucide-react";
import { useAuth } from "../context/AuthProvider";
import { useNavigate } from "react-router-dom";
import { db } from "../firebase";
import { doc, updateDoc, collection, getDocs, getDoc } from "firebase/firestore";
import { formatProjectTitle } from "../utils/formatProjectTitle";

export default function ProjectDashboard() {
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [workspace, setWorkspace] = useState("");
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [hasAccess, setHasAccess] = useState(true);
  const [accessChecked, setAccessChecked] = useState(false);
  const [accessReason, setAccessReason] = useState("");
  const [updatingUserId, setUpdatingUserId] = useState(null);
  const [updatingRoleUserId, setUpdatingRoleUserId] = useState(null);
  const [updatingDepartment, setUpdatingDepartment] = useState(null); // Track which department is being updated
  const [userDepartments, setUserDepartments] = useState({}); // Map email -> department
  const [selectedDepartment, setSelectedDepartment] = useState(""); // Department filter
  const [showDepartmentModal, setShowDepartmentModal] = useState(false); // Modal state
  
  // Filter for active/inactive users
  const [showInactive, setShowInactive] = useState(false);

  const resolveWorkspaceCollectionPath = (workspaceKey) => {
    const key = (workspaceKey || "").trim();
    if (!key) return { root: "workspaces", workspaceId: "ai-apps-dashboard" };
    if (key.includes("/")) {
      const parts = key.split("/");
      return { root: parts[0], workspaceId: parts[1] };
    }
    return { root: "workspaces", workspaceId: key.replace(/\s+/g, "-") };
  };

  // Helper function to check user access
  const checkUserAccess = (usersData, workspaceKey) => {
    if (currentUser) {
      // Admins have access to all projects
      if (currentUser.role === "admin" || currentUser.role === "super-admin") {
        setHasAccess(true);
      } else {
        const currentUserInProject = usersData.find(
          (u) => u.id === currentUser.uid || u.email === currentUser.email
        );
        
        if (!currentUserInProject) {
          setHasAccess(false);
          setAccessReason("not_assigned");
        } else if (currentUserInProject.isActive === false) {
          setHasAccess(false);
          setAccessReason("inactive");
        } else {
          setHasAccess(true);
        }
      }
    } else {
      setHasAccess(false);
      setAccessReason("not_authenticated");
    }
  };


  useEffect(() => {
    const fetchAndCheckAccess = async () => {
      const workspaceKey = localStorage.getItem("workspaceKey");
      const projectFirebaseConfigRaw = localStorage.getItem("projectFirebaseConfig");
      const projectFirebaseConfig = projectFirebaseConfigRaw
        ? JSON.parse(projectFirebaseConfigRaw)
        : null;

      if (workspaceKey) {
        try {
          const { getProjectUsers } = await import("../utils/getProjectUsers");
          const res = await getProjectUsers(workspaceKey, currentUser, projectFirebaseConfig);

          // Use allUsers for admins, users for regular users
          const freshUsers =
            currentUser?.role === "admin" || currentUser?.role === "super-admin"
              ? res.allUsers
              : res.users;
          
          // Set default active status to true if not defined
          const usersWithDefaults = freshUsers.map(u => ({
            ...u,
            isActive: u.isActive !== undefined ? u.isActive : true, // Default to active
            role: u.role || "user",
          }));
          
          // Remove duplicates based on user id (keep first occurrence)
          const uniqueUsers = usersWithDefaults.filter((user, index, self) => 
            index === self.findIndex((u) => u.id === user.id || (u.email === user.email && user.email))
          );
          
          // Set all users (we'll filter by isActive in the UI)
          setUsers(uniqueUsers);
          setWorkspace(workspaceKey);
          
          // Update localStorage with fresh data
          localStorage.setItem("projectUsers", JSON.stringify(uniqueUsers));

          // Fetch departments for all users
          fetchUserDepartments(uniqueUsers);

          // Check if current user has access to this project
          checkUserAccess(uniqueUsers, workspaceKey);

        } catch (error) {
          console.error("Error fetching users:", error);
          
          // Fallback to localStorage if backend fails
          const stored = localStorage.getItem("projectUsers");
          if (stored) {
            const parsedUsers = JSON.parse(stored);
            const usersWithDefaults = parsedUsers.map(u => ({
              ...u,
              isActive: u.isActive !== undefined ? u.isActive : true,
              role: u.role || "user",
            }));
            
            // Remove duplicates based on user id
            const uniqueUsers = usersWithDefaults.filter((user, index, self) => 
              index === self.findIndex((u) => u.id === user.id || (u.email === user.email && user.email))
            );
            
            setUsers(uniqueUsers);
            setWorkspace(workspaceKey);
            
            // Fetch departments for all users
            fetchUserDepartments(uniqueUsers);
            
            // Check access with fallback data
            checkUserAccess(uniqueUsers, workspaceKey);
          }
        }
      }

      setLoading(false);
      setAccessChecked(true);
    };

    fetchAndCheckAccess();
  }, [currentUser]);

  const handleRoleChange = async (userId, newRole) => {
    setUpdatingRoleUserId(userId);
    try {
      const currentWorkspace = localStorage.getItem("workspaceKey");
      if (!currentWorkspace) {
        console.error("Workspace not found");
        return;
      }

      const { root, workspaceId } = resolveWorkspaceCollectionPath(currentWorkspace);

      // Match getProjectUsers behavior: try users collection first, then profiles.
      const usersDocRef = doc(db, root, workspaceId, "users", userId);
      try {
        await updateDoc(usersDocRef, { role: newRole, updatedAt: new Date() });
      } catch (err) {
        const profilesDocRef = doc(db, root, workspaceId, "profiles", userId);
        await updateDoc(profilesDocRef, { role: newRole, updatedAt: new Date() });
      }

      const updatedUsers = users.map((u) =>
        u.id === userId ? { ...u, role: newRole } : u
      );
      setUsers(updatedUsers);
      localStorage.setItem("projectUsers", JSON.stringify(updatedUsers));
    } catch (error) {
      console.error("Error updating user role:", error);
    } finally {
      setUpdatingRoleUserId(null);
    }
  };

  // 🔹 Fetch departments for users from employees collection
  // Uses exact email match first, then falls back to email prefix (before @)
  // so that different domains for the same person share the same department.
  const fetchUserDepartments = async (usersList) => {
    try {
      const departmentsMap = {};
      
      // Fetch all employees at once
      const employeesRef = collection(db, "cynetdatabase", "root", "employees");
      const employeesSnap = await getDocs(employeesRef);
      
      // Create maps:
      // - email -> department (exact match)
      // - prefix -> department (fallback when exact email not found)
      const emailToDepartment = {};
      const prefixToDepartment = {};
      
      employeesSnap.docs.forEach((doc) => {
        const data = doc.data();
        if (data.email && data.department && typeof data.department === "string") {
          const normalizedEmail = data.email.toLowerCase().trim();
          const department = data.department.trim();
          const prefix = normalizedEmail.split("@")[0];

          if (department) {
            emailToDepartment[normalizedEmail] = department;
            // Only set prefix mapping if not already set; first one wins.
            if (!prefixToDepartment[prefix]) {
              prefixToDepartment[prefix] = department;
            }
          }
        }
      });
      
      // Map departments for each user:
      // 0) Use department already present on the user record (if any)
      // 1) Try exact email match
      // 2) If not found, try prefix-based match
      usersList.forEach((user) => {
        if (user?.department && typeof user.department === "string" && user.department.trim()) {
          const emailKey = (user.email || "").toLowerCase().trim();
          if (emailKey) {
            departmentsMap[emailKey] = user.department.trim();
          }
          return;
        }
        if (user.email && typeof user.email === "string") {
          const normalizedEmail = user.email.toLowerCase().trim();
          const prefix = normalizedEmail.split("@")[0];
          
          const deptExact = emailToDepartment[normalizedEmail];
          const deptByPrefix = prefixToDepartment[prefix];
          
          departmentsMap[normalizedEmail] = deptExact || deptByPrefix || "—";
        }
      });

      // Final fallback: if still missing, try the global user profile doc
      // at workspaces/ai-apps-dashboard/users/{uid} (this is where Auth bootstrap writes department).
      const missingDeptUsers = usersList.filter((u) => {
        const emailKey = (u.email || "").toLowerCase().trim();
        if (!emailKey) return false;
        return !departmentsMap[emailKey] || departmentsMap[emailKey] === "—";
      });

      await Promise.all(
        missingDeptUsers.map(async (u) => {
          const emailKey = (u.email || "").toLowerCase().trim();
          if (!emailKey) return;

          const uid = u.id;
          if (!uid) return;

          try {
            const globalUserRef = doc(db, "workspaces", "ai-apps-dashboard", "users", uid);
            const globalUserSnap = await getDoc(globalUserRef);
            if (!globalUserSnap.exists()) return;

            const globalDept = globalUserSnap.data()?.department;
            if (typeof globalDept === "string" && globalDept.trim()) {
              departmentsMap[emailKey] = globalDept.trim();
            }
          } catch (err) {
            console.error("Error fetching global user department:", err);
          }
        })
      );
      
      setUserDepartments(departmentsMap);
    } catch (err) {
      console.error("Error fetching user departments:", err);
    }
  };


  const handleAction = async (userId, action) => {
    setUpdatingUserId(userId);

    try {
      // Get current workspace from localStorage
      const currentWorkspace = localStorage.getItem("workspaceKey");
      
      if (!currentWorkspace) {
        console.error("Workspace not found");
        setUpdatingUserId(null);
        return;
      }

      const userRef = doc(db, "workspaces", currentWorkspace, "users", userId);

      // Update user status in Firestore
      const newStatus = action === "active" ? true : action === "inactive" ? false : null;
      
      if (newStatus !== null) {
        await updateDoc(userRef, {
          isActive: newStatus,
          updatedAt: new Date()
        });
        
        // Update local state
        const updatedUsers = users.map((u) =>
          u.id === userId ? { ...u, isActive: newStatus } : u
        );
        setUsers(updatedUsers);
        
        // Update localStorage
        localStorage.setItem("projectUsers", JSON.stringify(updatedUsers));
      }
    } catch (error) {
      console.error("Error updating user:", error);
    } finally {
      setUpdatingUserId(null);
    }
  };

  // Get unique departments from userDepartments
  const uniqueDepartments = Array.from(
    new Set(Object.values(userDepartments).filter(dept => dept && dept !== "—"))
  ).sort();

  // Handle department-wise activation/deactivation
  const handleDepartmentAction = async (department, action) => {
    setUpdatingDepartment(department);

    try {
      // Get current workspace from localStorage
      const currentWorkspace = localStorage.getItem("workspaceKey");
      
      if (!currentWorkspace) {
        console.error("Workspace not found");
        setUpdatingDepartment(null);
        return;
      }

      // Find all users in this department
      const departmentUsers = users.filter((u) => {
        const userDept = userDepartments[u.email?.toLowerCase()?.trim()] || "—";
        return userDept === department;
      });

      if (departmentUsers.length === 0) {
        setUpdatingDepartment(null);
        return;
      }

      const newStatus = action === "active" ? true : action === "inactive" ? false : null;
      
      if (newStatus !== null) {
        // Update all users in the department
        const updatePromises = departmentUsers.map((u) => {
          const userRef = doc(db, "workspaces", currentWorkspace, "users", u.id);
          return updateDoc(userRef, {
            isActive: newStatus,
            updatedAt: new Date()
          });
        });

        await Promise.all(updatePromises);
        
        // Update local state
        const updatedUsers = users.map((u) => {
          const userDept = userDepartments[u.email?.toLowerCase()?.trim()] || "—";
          if (userDept === department) {
            return { ...u, isActive: newStatus };
          }
          return u;
        });
        setUsers(updatedUsers);
        
        // Update localStorage
        localStorage.setItem("projectUsers", JSON.stringify(updatedUsers));
      }
    } catch (error) {
      console.error("Error updating department users:", error);
    } finally {
      setUpdatingDepartment(null);
    }
  };

  // Calculate department statistics
  const getDepartmentStats = () => {
    const stats = {};
    uniqueDepartments.forEach((dept) => {
      const deptUsers = users.filter((u) => {
        const userDept = userDepartments[u.email?.toLowerCase()?.trim()] || "—";
        return userDept === dept;
      });
      const activeCount = deptUsers.filter((u) => u.isActive !== false).length;
      const inactiveCount = deptUsers.filter((u) => u.isActive === false).length;
      const allActive = deptUsers.length > 0 && activeCount === deptUsers.length;
      const allInactive = deptUsers.length > 0 && inactiveCount === deptUsers.length;
      
      stats[dept] = {
        total: deptUsers.length,
        active: activeCount,
        inactive: inactiveCount,
        allActive,
        allInactive,
        mixed: deptUsers.length > 0 && !allActive && !allInactive
      };
    });
    return stats;
  };

  const departmentStats = getDepartmentStats();

  // Filter users based on active/inactive status, search, and department
  const filteredUsers = users.filter((u) => {
    // Filter by active/inactive status
    if (showInactive) {
      // Show only inactive users
      if (u.isActive !== false) return false;
    } else {
      // Show only active users (default)
      if (u.isActive === false) return false;
    }
    
    // Filter by department
    if (selectedDepartment) {
      const userDept = userDepartments[u.email?.toLowerCase()?.trim()] || "—";
      if (userDept !== selectedDepartment) return false;
    }
    
    // Filter by search query
    const q = search.toLowerCase();
    return (
      (u.displayName && u.displayName.toLowerCase().includes(q)) ||
      (u.fullName && u.fullName.toLowerCase().includes(q)) ||
      (u.name && u.name.toLowerCase().includes(q)) ||
      (u.email && u.email.toLowerCase().includes(q))
    );
  });
  
  // Get inactive users count for display
  const inactiveUsersCount = users.filter(u => u.isActive === false).length;

  // Pagination calculations
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentUsers = filteredUsers.slice(startIndex, endIndex);

  // Reset to page 1 when search or department filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [search, selectedDepartment]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        pages.push(1, 2, 3, 4, '...', totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
      }
    }
    
    return pages;
  };

  if (loading || !accessChecked)
    return (
      <div className="flex items-center justify-center h-[60vh] text-gray-500 dark:text-gray-300">
        Loading users...
      </div>
    );

  // Permission Denied Screen
  if (!hasAccess) {
    const getAccessMessage = () => {
      switch (accessReason) {
        case "inactive":
          return {
            title: "Account Inactive",
            description: "You don't have permission to access this project.",
            details: `Your account status for ${workspace || "this project"} is currently inactive. Your access has been temporarily disabled.`,
            helpText: "Contact your project administrator to reactivate your account for this project."
          };
        case "not_assigned":
          return {
            title: "Not Assigned",
            description: "You are not assigned to this project.",
            details: `You are not a member of ${workspace || "this project"}. Only assigned team members can access project details.`,
            helpText: "Contact your project administrator to be added to this project."
          };
        case "not_authenticated":
          return {
            title: "Authentication Required",
            description: "You need to be logged in to access this project.",
            details: "Please sign in with your account to view project details.",
            helpText: "If you're having trouble logging in, contact your administrator."
          };
        default:
          return {
            title: "Access Denied",
            description: "You don't have permission to access this project.",
            details: "Please contact your project administrator for assistance.",
            helpText: "Contact your administrator if you believe this is an error."
          };
      }
    };

    const message = getAccessMessage();

    return (
      <div className="flex items-center justify-center min-h-[70vh] p-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md w-full bg-white dark:bg-gray-900 rounded-2xl shadow-xl p-8 text-center"
        >
          <div className="flex justify-center mb-6">
            <div className="p-4 bg-red-100 dark:bg-red-900/30 rounded-full">
              <ShieldAlert className="w-16 h-16 text-red-600 dark:text-red-400" />
            </div>
          </div>
          
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
            {message.title}
          </h2>
          
          <p className="text-gray-600 dark:text-gray-400 mb-2">
            {message.description}
          </p>
          
          <p className="text-sm text-gray-500 dark:text-gray-500 mb-6">
            {message.details}
          </p>

          <div className="flex flex-col gap-3">
            <button
              onClick={() => navigate(-1)}
              className="w-full px-4 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition flex items-center justify-center gap-2"
            >
              <ChevronLeft size={18} />
              Go Back
            </button>
            
            <button
              onClick={() => navigate(currentUser?.role === "admin" ? "/admin/projects" : "/")}
              className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition"
            >
              View All Projects
            </button>
          </div>

          <div className="mt-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
            <div className="flex items-start gap-2">
              <Lock className="w-5 h-5 text-yellow-600 dark:text-yellow-500 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-yellow-800 dark:text-yellow-300 text-left">
                <strong>Need access?</strong> {message.helpText}
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-6xl mx-auto p-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition flex items-center gap-2 text-gray-600 dark:text-gray-300"
            title="Go Back"
          >
            <ChevronLeft size={24} />
          </button>
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
            <span className="text-indigo-500">{workspace ? formatProjectTitle(workspace) : "N/A"}</span> Users
          </h2>
        </div>
        {/* Toggle Active/Inactive Users - Icon Buttons */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowInactive(false)}
            className={`relative p-2.5 rounded-lg transition group ${
              !showInactive
                ? 'bg-indigo-600 text-white'
                : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
            }`}
            title="Active Users"
          >
            <CheckCircle size={20} />
            {/* Tooltip */}
            <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
              Active Users
            </span>
          </button>
          <button
            onClick={() => setShowInactive(true)}
            className={`relative p-2.5 rounded-lg transition group ${
              showInactive
                ? 'bg-indigo-600 text-white'
                : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
            }`}
            title="Inactive Users"
          >
            <XCircle size={20} />
            {inactiveUsersCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                {inactiveUsersCount}
              </span>
            )}
            {/* Tooltip */}
            <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
              Inactive Users
            </span>
          </button>
        </div>
      </div>

      {/* Stats */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="p-6 bg-gradient-to-r from-indigo-50 to-indigo-100 dark:from-indigo-900 dark:to-indigo-800 rounded-2xl shadow-md flex items-center justify-between"
      >
        <div>
          <h2 className="text-sm font-medium text-gray-600 dark:text-gray-300">
            Total Users
          </h2>
          <p className="text-3xl font-bold text-gray-900 dark:text-white mt-1">
            {showInactive ? inactiveUsersCount : users.filter(u => u.isActive !== false).length}
          </p>
          <p className="text-sm text-indigo-600 mt-1">{showInactive ? 'Inactive Members' : 'Active Members'}</p>
        </div>
        <Users className="text-indigo-500" size={38} />
      </motion.div>

      {/* Department Modal */}
      <AnimatePresence>
        {showDepartmentModal && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowDepartmentModal(false)}
              className="fixed inset-0 bg-black/50 z-40"
            />
            
            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
            >
              <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                {/* Modal Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                  <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-100">
                    Department Access Control
                  </h3>
                  <button
                    onClick={() => setShowDepartmentModal(false)}
                    className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition"
                  >
                    <X size={20} className="text-gray-600 dark:text-gray-400" />
                  </button>
                </div>

                {/* Modal Content */}
                <div className="p-6 overflow-y-auto flex-1">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {uniqueDepartments.map((dept) => {
                      const stats = departmentStats[dept];
                      const isUpdating = updatingDepartment === dept;
                      const isAllActive = stats?.allActive;
                      const isAllInactive = stats?.allInactive;
                      
                      return (
                        <div
                          key={dept}
                          className={`p-4 border rounded-lg transition ${
                            isUpdating
                              ? 'border-blue-300 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/20 opacity-75'
                              : 'border-gray-200 dark:border-gray-700 hover:border-indigo-300 dark:hover:border-indigo-600'
                          }`}
                        >
                          <div className="flex flex-col gap-3">
                            <div>
                              <h4 className="font-medium text-gray-800 dark:text-gray-100">{dept}</h4>
                              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                                {stats?.active || 0} Active / {stats?.total || 0} Total
                              </p>
                            </div>
                            <div className="flex flex-col gap-2">
                              {isUpdating ? (
                                <span className="text-xs text-blue-600 dark:text-blue-400 text-center py-2">Updating...</span>
                              ) : (
                                <>
                                  <button
                                    onClick={() => handleDepartmentAction(dept, "active")}
                                    disabled={isAllActive || isUpdating}
                                    className={`w-full px-3 py-2 text-xs font-medium rounded-lg transition ${
                                      isAllActive
                                        ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300 cursor-not-allowed'
                                        : 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300 hover:bg-green-100 dark:hover:bg-green-900/50'
                                    }`}
                                  >
                                    Activate All
                                  </button>
                                  <button
                                    onClick={() => handleDepartmentAction(dept, "inactive")}
                                    disabled={isAllInactive || isUpdating}
                                    className={`w-full px-3 py-2 text-xs font-medium rounded-lg transition ${
                                      isAllInactive
                                        ? 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-400 cursor-not-allowed'
                                        : 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-900/50'
                                    }`}
                                  >
                                    Deactivate All
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Modal Footer */}
                <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end">
                  <button
                    onClick={() => setShowDepartmentModal(false)}
                    className="px-6 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition font-medium"
                  >
                    Close
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Search Bar and Department Filter */}
      <div className="flex flex-wrap gap-4 items-center">
        <div className="relative max-w-sm flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-3 text-gray-400" size={18} />
          <input
            type="text"
            placeholder="Search users by name or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:outline-none dark:bg-gray-800 dark:text-gray-100"
          />
        </div>
        
        {/* Department Filter */}
        <div className="min-w-[200px]">
          <select
            value={selectedDepartment}
            onChange={(e) => setSelectedDepartment(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg 
            focus:ring-2 focus:ring-indigo-500 focus:outline-none dark:bg-gray-800 dark:text-gray-100
            bg-white text-gray-900 dark:text-gray-100 cursor-pointer"
          >
            <option value="">All Departments</option>
            {uniqueDepartments.map((dept) => (
              <option key={dept} value={dept}>
                {dept}
              </option>
            ))}
          </select>
        </div>

        {/* Department Access Control Button */}
        {uniqueDepartments.length > 0 && (
          <button
            onClick={() => setShowDepartmentModal(true)}
            className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium transition flex items-center gap-2 shadow-md whitespace-nowrap"
          >
            <Building2 size={18} />
            Manage Department Access
          </button>
        )}
      </div>

      {/* Users Table */}
      <div className="mt-4 bg-white dark:bg-gray-900 rounded-2xl shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">
          {showInactive ? 'Inactive Users' : 'Active Users'}
        </h3>

        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200">
                <th className="px-4 py-3 text-left text-sm font-semibold">Name</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Email</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Department</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Role</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Status</th>
                <th className="px-4 py-3 text-right text-sm font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {currentUsers.map((u, index) => (
                <tr
                  key={`user-${u.id || u.email || index}`}
                  className={`hover:bg-gray-50 dark:hover:bg-gray-800 transition ${
                    updatingUserId === u.id ? 'opacity-50 pointer-events-none' : ''
                  }`}
                >
                  <td className="px-4 py-3 text-gray-800 dark:text-gray-100">
                    {u.displayName || u.fullName || u.name || "Unnamed"}
                  </td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-300">
                    {u.email || "—"}
                  </td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-300">
                    {u.department ||
                      userDepartments[u.email?.toLowerCase()?.trim()] ||
                      "—"}
                  </td>
                  <td className="px-4 py-3">
                    {updatingRoleUserId === u.id ? (
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                        Updating...
                      </span>
                    ) : (
                      <select
                        value={u.role || "user"}
                        onChange={(e) => handleRoleChange(u.id, e.target.value)}
                        disabled={updatingRoleUserId === u.id}
                        className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600
                        bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100
                        focus:outline-none focus:ring-2 focus:ring-indigo-500 transition
                        disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <option value="user">User</option>
                        <option value="admin">Admin</option>
                        <option value="super-admin">Super Admin</option>
                      </select>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {updatingUserId === u.id ? (
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                        Updating...
                      </span>
                    ) : (
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full ${
                          u.isActive
                            ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                            : "bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300"
                        }`}
                      >
                        {u.isActive ? "Active" : "Inactive"}
                      </span>
                    )}
                  </td>

                  {/* Toggle Switch */}
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => handleAction(u.id, u.isActive ? "inactive" : "active")}
                      disabled={updatingUserId === u.id}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 ${
                        u.isActive
                          ? "bg-indigo-600"
                          : "bg-gray-300 dark:bg-gray-600"
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          u.isActive ? "translate-x-6" : "translate-x-1"
                        }`}
                      />
                    </button>
                  </td>
                </tr>
              ))}

              {filteredUsers.length === 0 && (
                <tr>
                  <td
                    colSpan="6"
                    className="px-4 py-6 text-center text-gray-500 dark:text-gray-400"
                  >
                    No matching users found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        {filteredUsers.length > 0 && totalPages > 1 && (
          <div className="mt-6 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 pt-4">
            <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
              Showing {startIndex + 1} to {Math.min(endIndex, filteredUsers.length)} of {filteredUsers.length} users
            </div>
            
            <div className="flex items-center gap-2">
              {/* Previous Button */}
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className={`p-2 rounded-lg border ${
                  currentPage === 1
                    ? 'border-gray-300 dark:border-gray-600 text-gray-400 dark:text-gray-600 cursor-not-allowed'
                    : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                }`}
              >
                <ChevronLeft size={18} />
              </button>

              {/* Page Numbers */}
              <div className="flex items-center gap-1">
                {getPageNumbers().map((page, index) => (
                  page === '...' ? (
                    <span
                      key={`ellipsis-${index}`}
                      className="px-3 py-1.5 text-gray-500 dark:text-gray-400"
                    >
                      ...
                    </span>
                  ) : (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                        currentPage === page
                          ? 'bg-indigo-600 text-white'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                      }`}
                    >
                      {page}
                    </button>
                  )
                ))}
              </div>

              {/* Next Button */}
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`p-2 rounded-lg border ${
                  currentPage === totalPages
                    ? 'border-gray-300 dark:border-gray-600 text-gray-400 dark:text-gray-600 cursor-not-allowed'
                    : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                }`}
              >
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
        )}
      </div>

    </div>
  );
}


































